import { type FormEvent, useState } from "react";
import { UserPlus } from "lucide-react";

import { UserAvatar } from "@/components/UserAvatar";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { BoardMember } from "@/features/board/types";

type MemberDialogProps = {
  members: BoardMember[];
  open: boolean;
  owner: boolean;
  onAdd: (username: string) => Promise<BoardMember>;
  onClose: () => void;
  onRemove: (userId: string) => Promise<void>;
};

export const MemberDialog = ({
  members,
  open,
  owner,
  onAdd,
  onClose,
  onRemove,
}: MemberDialogProps) => {
  const [username, setUsername] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onAdd(username.trim().toLowerCase());
      setUsername("");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The member could not be added.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (userId: string) => {
    setSaving(true);
    setError(null);
    try {
      await onRemove(userId);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The member could not be removed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      description={owner ? "Add registered teammates by their exact username." : "People with access to this project."}
      onClose={onClose}
      open={open}
      title="Project members"
    >
      <div className="flex flex-col gap-5">
        {owner ? (
          <form className="flex flex-col gap-3 sm:flex-row sm:items-end" onSubmit={add}>
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Label htmlFor="member-username">Username</Label>
              <Input
                autoCapitalize="none"
                id="member-username"
                maxLength={24}
                minLength={3}
                name="username"
                onChange={(event) => setUsername(event.target.value.toLowerCase())}
                placeholder="teammate"
                required
                value={username}
              />
            </div>
            <Button className="shrink-0 pl-2 pr-3" disabled={saving} type="submit">
              <UserPlus className="size-4 shrink-0 stroke-current" />
              Add member
            </Button>
          </form>
        ) : null}
        {error ? <Alert className="border-0 bg-red-50 text-red-800 ring-1 ring-red-900/10">{error}</Alert> : null}
        <ul className="divide-y divide-neutral-950/5" role="list">
          {members.map((member) => (
            <li className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0" key={member.id}>
              <div className="flex min-w-0 items-center gap-3">
                <UserAvatar image={member.image} name={member.name} />
                <div className="min-w-0">
                  <p className="truncate text-base font-medium text-neutral-950 sm:text-sm">{member.name}</p>
                  <p className="truncate text-base text-neutral-500 sm:text-sm">@{member.username}</p>
                </div>
              </div>
              {member.role === "owner" ? (
                <Badge variant="secondary">Owner</Badge>
              ) : owner ? (
                <Button disabled={saving} onClick={() => void remove(member.id)} size="sm" type="button" variant="ghost">
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    </Dialog>
  );
};
